import { useState, Dispatch, FC, SetStateAction, useEffect } from 'react';
import { Box, Button, CircularProgress, Typography, IconButton, Accordion, AccordionSummary, AccordionDetails, List, ListItem, ListItemText } from '@mui/material';
import { Link } from 'react-router-dom';
import { ExpandMore, ExpandLess, CloseSharp, CheckCircleRounded } from '@mui/icons-material';
import { CalculationLoaderProps, IIneligibleSettingForCalculationLoading } from '..';
import { CalcIneligibleState } from '../../../../../reducer/calcIneligibleReducer';
import { CalculateIneligible, setCalcIneligibleLoader } from '../../../../../actions/calcIneligibleActions';
import { CalcARStatus } from '../../../../../interfaces/calcARStatus';
import colors from '../../../../../utility/colors';
import styles from '../styles';
import ErrorIcon from '@mui/icons-material/Error';
import { generateRandomString } from '../../../../../utility/helper';
import { Client } from '@stomp/stompjs';

export interface SubscribingComponentProps extends CalculationLoaderProps {
  state: CalcIneligibleState;
  dispatch: Dispatch<CalculateIneligible>;
  setCalcARStatus: Dispatch<SetStateAction<CalcARStatus | null>>;
  ineligibles: IIneligibleSettingForCalculationLoading[];
  stompClient: {
    arClient: Client | null;
    invClient: Client | null;
  };
}

const SubscribingComponent: FC<SubscribingComponentProps> = (props) => {
  const { state, dispatch, setCalcARStatus, ineligibles, stompClient } = props;
  const [isAccordionExpanded, setIsAccordionExpanded] = useState<boolean>(true);
  const [arSubscriptionId, setARSubscriptionId] = useState<string | null>(null);
  const [invSubscriptionId, setInvSubscriptionId] = useState<string | null>(null);

  const listItemRef = (node: HTMLLIElement) => {
    if (node !== null) {
      node.scrollIntoView({
        behavior  : 'smooth',
        block     : 'end',
      });
    }
  }

  const isCurrent = (item: IIneligibleSettingForCalculationLoading, index: number, array: IIneligibleSettingForCalculationLoading[]) => {
    if (index === 0) {
      return item.isLoading;
    }

    return item.isLoading && !array[index - 1].isLoading;
  }

  const hideLoader = () => {
    dispatch(setCalcIneligibleLoader({
      ...state,
      show: false
    }));
  }

  const cancelCalculation = () => {
    dispatch(setCalcIneligibleLoader({
      ...state,
      cancelled: true
    }))
  }

  useEffect(() => { /* for upc-level ineligible calculation */
    if (!state.isParentClientLevel) { return; }
    subscribeToStompClientForUPC();
  }, [state, stompClient]); /* resubscribe when reloaded */

  /* AR Subscription */
  useEffect(() => {
    if (state.isParentClientLevel) return;
    if (!state.isLoading) return;
    if (arSubscriptionId) return;

    const generatedId = generateRandomString(10);
    const arSubscription = stompClient.arClient?.subscribe('/topic/status', (message) => {
      const body = message?.body;
      if (!body) return;

      const calcARStatus = JSON.parse(body);
      if (state.selectedClientId !== calcARStatus.borrowerId) return;
      if (state.type !== 'AR') return;
      setCalcARStatus(calcARStatus);
    }, { id: generatedId });

    if (arSubscription?.id) setARSubscriptionId(arSubscription.id);
  }, [state.isLoading, stompClient]);

  /* Inventory Subscription */
  useEffect(() => {
    if (state.isParentClientLevel) return;
    if (!state.isLoading) return;
    if (invSubscriptionId) return;

    const generatedId = generateRandomString(10);
    const inventorySubscription = stompClient.invClient?.subscribe('/topic/status', (message) => {
      const body = message?.body;
      if (!body) return;

      const calcInvStatus = JSON.parse(body);
      if (state.selectedClientId !== calcInvStatus.borrowerId) return;
      if (state.type !== 'Inventory') return;
      setCalcARStatus(calcInvStatus);
    }, { id: generatedId });

    if (inventorySubscription?.id) setInvSubscriptionId(inventorySubscription.id);
  }, [state.isLoading, stompClient]);

  useEffect(() => {
    if (state.title === "Calculation Finished") {
      if (arSubscriptionId !== null) {
        stompClient.arClient?.unsubscribe(arSubscriptionId);
        setARSubscriptionId(null);
      }

      if (invSubscriptionId !== null) {
        stompClient.invClient?.unsubscribe(invSubscriptionId);
        setInvSubscriptionId(null);
      }
    }
  }, [state.title, arSubscriptionId, invSubscriptionId]);

  const subscribeToStompClientForUPC = () => {
    const titlesForFinishedCalculation = ['Calculation Finished', 'Calculation Failed'];
    const isInitiallyDispatched = !state.hasInitiatedSubscription && !titlesForFinishedCalculation.includes(state.title);
    if (!isInitiallyDispatched) { return; }
    const generatedId = generateRandomString(10);
    const subscription = stompClient.arClient?.subscribe('/topic/status', (message) => {
      const body = message?.body;
      if (!body) { return; }

      const calcARStatus = JSON.parse(body);

      if (state.selectedClientId !== calcARStatus.borrowerId) return;
      setCalcARStatus(calcARStatus)
    }, { id: generatedId });

    if (!subscription?.id) { return; }
    setARSubscriptionId(subscription?.id);
    dispatch(setCalcIneligibleLoader({ ...state, hasInitiatedSubscription: true, isLoading: true }));
  };

  const getHeaderLabelStatus = () => {
    if (state.queuedReason) { return 'Calculation Queued'; }
    if (state.failed) { return 'Calculation Failed'; }
    if (state.isLoading) {
      return `Calculating ${state.title.toLowerCase().includes('borrowing') ? 'Report' : 'Ineligibles'}`;
    }
    return 'Calculation Completed';
  };


  /**
   * Gets the status icon for an ineligible item based on its properties.
   * @param item The ineligible item to determine the status icon for.
   * @returns The status icon element.
   */
  const getStatusIcon = (item: IIneligibleSettingForCalculationLoading) => {
    if (state.failed) { return <ErrorIcon htmlColor={colors.ERROR} sx={styles.iconLeftSpacing}/>; }
    if (state.isLoading && item.isLoading) {
      return (
        <CircularProgress
          tabIndex={0}
          aria-label='Loading icon'
          variant="indeterminate"
          disableShrink
          sx={{...styles.spinner, ...styles.iconLeftSpacing}}
          size={20}
          thickness={5}
        />
      );
    }
    return <CheckCircleRounded tabIndex={0} aria-label='Green checked icon' htmlColor={colors.SUCCESS} sx={styles.iconLeftSpacing}/>;
  };

  return <Box>
    <Box sx={styles.header}>
      <Typography tabIndex={0}>{getHeaderLabelStatus()}</Typography>
      <Box>
        <IconButton aria-label='Expand icon' sx={styles.action} onClick={() => setIsAccordionExpanded(!isAccordionExpanded)}>
          {
            !isAccordionExpanded ? <ExpandMore htmlColor={colors.WHITE} /> : <ExpandLess htmlColor={colors.WHITE} />
          }
        </IconButton>
        <IconButton aria-label='Close icon' sx={styles.action} onClick={(event) => {
          event.stopPropagation();
          hideLoader();
        }}>
          <CloseSharp htmlColor={colors.WHITE} />
        </IconButton>
      </Box>
    </Box>

    <Accordion
        defaultExpanded={true}
        expanded={isAccordionExpanded}
        sx={styles.contentWrapper}
    >
      <AccordionSummary
        aria-controls="panelcl-content"
        id="panelcl-header"
        hidden
        sx={{ ...styles.accordionSummary, ...(!state.isLoading && styles.hidden) }}
      >
        <Box sx={{...styles.content, ...styles.contentHeader }}>
          <Typography tabIndex={0} sx={styles.smallFont}>{ state.queuedReason ? state.queuedReason : (state.title ? state.title : 'Calculation Started...') }</Typography>
          <Button variant='text' sx={{...styles.smallFont, p: 0, display:'none'}} onClick={() => cancelCalculation()}>CANCEL</Button>
        </Box>
      </AccordionSummary>

      <AccordionDetails sx={styles.accordionDetails}>
        <Box>
          <Box sx={{...styles.content}}>
            {state.selectedClientId ?
            <List dense={true} sx={{width: 1}}>
              {ineligibles.map((item, index, array) => 
                <ListItem
                  key={item.code}
                  ref={isCurrent(item, index, array) ? listItemRef : null}
                  secondaryAction={getStatusIcon(item)}
                >
                  <ListItemText tabIndex={0}>{item.description}</ListItemText>
                </ListItem>
              )}
            </List>
            : <ListItem
            secondaryAction={
              state.isLoading ? 
              <CircularProgress
                tabIndex={0}
                aria-label='Loading icon'
                variant="indeterminate"
                disableShrink
                sx={{...styles.spinner, ...styles.iconLeftSpacing}}
                size={20}
                thickness={5}
              />
            : <CheckCircleRounded tabIndex={0} aria-label='Green checked icon' htmlColor={colors.SUCCESS} sx={styles.iconLeftSpacing}/>
            }
          >
            <ListItemText tabIndex={0}>{state.content}</ListItemText>
          </ListItem>
            }
          </Box>
        </Box>
      </AccordionDetails>

      </Accordion>
      { (!state.isLoading && state.link.length > 0) && <Box sx={styles.reportLink}>
          <Link to={state.link} onClick={hideLoader}>
            <Typography>Check Report Here</Typography>
          </Link>
        </Box>
      }
  </Box>;
}

export default SubscribingComponent;